import Color from '@arcgis/core/Color';
import esriConfig from '@arcgis/core/config';
import Extent from '@arcgis/core/geometry/Extent';
import { geodesicLength } from '@arcgis/core/geometry/geometryEngine';
import Point from '@arcgis/core/geometry/Point';
import Polygon from '@arcgis/core/geometry/Polygon';
import Polyline from '@arcgis/core/geometry/Polyline';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Map from '@arcgis/core/Map';
import PictureMarkerSymbol from '@arcgis/core/symbols/PictureMarkerSymbol';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import TextSymbol from '@arcgis/core/symbols/TextSymbol';
import MapView from '@arcgis/core/views/MapView';
import SceneView from '@arcgis/core/views/SceneView';

import { InjectionToken } from '@angular/core';

/**
 * Type defining the ESRI modules that services need.
 * This provides a clean abstraction with full type safety and IntelliSense.
 */
export type EsriModules = {
  config: typeof esriConfig;
  Map: typeof Map;
  MapView: typeof MapView;
  SceneView: typeof SceneView;
  Graphic: typeof Graphic;
  Point: typeof Point;
  Polyline: typeof Polyline;
  Extent: typeof Extent;
  SimpleMarkerSymbol: typeof SimpleMarkerSymbol;
  TextSymbol: typeof TextSymbol;
  GraphicsLayer: typeof GraphicsLayer;
  Polygon: typeof Polygon;
  SimpleLineSymbol: typeof SimpleLineSymbol;
  SimpleFillSymbol: typeof SimpleFillSymbol;
  Color: typeof Color;
  PictureMarkerSymbol: typeof PictureMarkerSymbol;
  geodesicLength: typeof geodesicLength;
};

/**
 * Injection token for ESRI modules.
 * Provides access to ESRI modules in a lazy-loaded manner to prevent runtime errors
 * when services are injected before ESRI modules are loaded.
 * Can be null if ESRI modules are not loaded.
 */
export const ESRI_MODULES_TOKEN = new InjectionToken<EsriModules | null>('ESRI_MODULES');

/**
 * Factory function to provide ESRI modules.
 * Returns the ESRI modules when available, otherwise returns null.
 */
export function esriModulesFactory(): EsriModules | null {
  if (typeof __esri !== 'undefined') {
    return {
      config: esriConfig,
      Map: Map,
      MapView: MapView,
      SceneView: SceneView,
      Graphic: Graphic,
      Point: Point,
      Polyline: Polyline,
      Extent: Extent,
      SimpleMarkerSymbol: SimpleMarkerSymbol,
      TextSymbol: TextSymbol,
      GraphicsLayer: GraphicsLayer,
      Polygon: Polygon,
      SimpleLineSymbol: SimpleLineSymbol,
      SimpleFillSymbol: SimpleFillSymbol,
      Color: Color,
      PictureMarkerSymbol: PictureMarkerSymbol,
      geodesicLength: geodesicLength,
    };
  }
  return null;
}
